import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { useToleranceStore } from './toleranceStore';
import { supabase } from '../lib/supabase';
import { authFetch, getAccessToken } from '../lib/authFetch';
import { checkSketchFile, sketchExt, sketchPathFromUrl } from '../utils/sketchFile';
import { reportError } from '../lib/sentry';

/**
 * Helper: build authenticated fetch options for the API. Returns null
 * if there's no session, so callers can early-return. The token comes
 * from the Supabase SDK (getAccessToken), not from the authStore copy,
 * which can be expired after the tab sat in the background.
 */
async function authedHeaders() {
  const token = await getAccessToken();
  if (!token) return null;
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
}

/**
 * Wraps fetch with a hard timeout so a hanging request (e.g. backend
 * down, proxy stalled) surfaces as a visible error instead of leaving
 * the caller's modal in `saving: true` forever. 15 s matches the
 * Vite proxy ceiling configured in vite.config.js.
 */
async function fetchWithTimeout(url, options = {}, ms = 15000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await authFetch(url, { ...options, signal: ctrl.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(
        `Anfrage hat ${ms / 1000}s lang nicht geantwortet. Läuft der Backend-Server (npm run dev:server)?`
      );
    }
    throw err;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Robust JSON-response reader.
 *
 * `await res.json()` blows up with "Unexpected end of JSON input" when
 * the server returns an empty body (proxy down, supabase returned an
 * undefined data with no error, etc.). This helper reads the body as
 * text first, parses JSON only when there's something to parse, and
 * turns failure modes into actionable error messages that name the
 * HTTP status and surface any server-provided detail.
 *
 * Returns the parsed body (or null when the body is empty and the
 * status is ok). Throws on non-2xx or on a non-JSON body.
 */
export async function readJsonOrThrow(res, context = 'Anfrage') {
  const text = await res.text();
  let body = null;
  if (text && text.trim().length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      throw new Error(
        `${context} fehlgeschlagen: Server hat keine gültige JSON-Antwort gesendet (HTTP ${res.status} ${res.statusText}). Vorschau: ${text.slice(0, 120)}`
      );
    }
  }
  if (!res.ok) {
    const detail = body?.error || body?.message || `HTTP ${res.status} ${res.statusText}`;
    throw new Error(`${context} fehlgeschlagen: ${detail}`);
  }
  if (body === null) {
    // 2xx with empty body — shouldn't happen for any of our routes
    // (they all res.json(data) on success). Surface explicitly so
    // we don't silently corrupt state.
    throw new Error(
      `${context} fehlgeschlagen: Server hat eine leere Antwort gesendet (HTTP ${res.status}). Bitte Server-Logs prüfen.`
    );
  }
  return body;
}

// Versions-Vorschau ohne zugehörige Berechnung (Projektwechsel): der
// Editor würde sonst gesperrt mit dem alten Stand stehen bleiben.
function endPreviewWithoutCalc() {
  if (useToleranceStore.getState().preview) useToleranceStore.getState().newProject();
}

// Running save, shared so saves never overlap (see saveCurrentCalculation).
let inFlightSave = null;

function isStale(get, project, calc) {
  return get().currentProject?.id !== project.id
    || (get().currentCalculation?.id ?? null) !== (calc?.id ?? null);
}

/**
 * Save the current tolerance chain once: PATCH the attached calc or
 * POST a new one into the current project.
 */
async function saveOnce(set, get) {
  const headers = await authedHeaders();
  const currentProject = get().currentProject;
  const currentCalculation = get().currentCalculation;
  if (!headers || !currentProject) return;

  const toleranceData = useToleranceStore.getState().getCalculationData();
  const name = useToleranceStore.getState().calculationName;
  // Snapshot of what goes over the wire. Edits made while the request
  // is in flight must keep the chain dirty, otherwise they would be
  // shown as saved and never reach the server.
  const snapshot = JSON.stringify({ name, data: toleranceData });

  set({ saving: true });
  try {
    let res;
    if (currentCalculation) {
      res = await authFetch(
        `/api/projects/${currentProject.id}/calculations/${currentCalculation.id}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ name, data: toleranceData })
        }
      );
    } else {
      res = await authFetch(`/api/projects/${currentProject.id}/calculations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, data: toleranceData })
      });
    }

    const savedCalc = await readJsonOrThrow(res, 'Berechnung speichern');

    // The user may have opened another calculation or project while
    // the request was running. Then only the list entry is updated;
    // pointing the editor back at this calc would let the next
    // auto-save overwrite it with the other chain.
    if (isStale(get, currentProject, currentCalculation)) {
      set(state => ({
        calculations: state.currentProject?.id !== currentProject.id
          ? state.calculations
          : currentCalculation
            ? state.calculations.map(c => c.id === savedCalc.id ? savedCalc : c)
            : [...state.calculations, savedCalc]
      }));
      return savedCalc;
    }

    // Sync the TR-XXX index back into the tolerance store so the
    // ProjectBar's strip switches from "TR-NEW" to the real number
    // immediately after the first save.
    const tol = useToleranceStore.getState();
    const unchanged = JSON.stringify({ name: tol.calculationName, data: tol.getCalculationData() }) === snapshot;
    useToleranceStore.setState({
      currentCalculationId: savedCalc.id,
      calculationIndex: savedCalc.calculation_index,
      ...(unchanged ? { isDirty: false } : {})
    });

    set(state => ({
      saveError: null,
      currentCalculation: savedCalc,
      calculations: currentCalculation
        ? state.calculations.map(c => c.id === savedCalc.id ? savedCalc : c)
        : [...state.calculations, savedCalc]
    }));

    return savedCalc;
  } catch (err) {
    console.error('Error saving calculation:', err);
    if (!isStale(get, currentProject, currentCalculation)) {
      set({ saveError: err?.message || 'Speichern fehlgeschlagen' });
    }
    throw err;
  } finally {
    set({ saving: false });
  }
}

const SKETCH_UPLOAD_FAILED = 'Skizze konnte nicht hochgeladen werden. Bitte später erneut versuchen.';

function sketchUrlOf(state, calcId) {
  if (state.currentCalculation?.id === calcId) return state.currentCalculation.sketch_url || null;
  return state.calculations.find((c) => c.id === calcId)?.sketch_url || null;
}

// Alte Skizzen-Datei aus dem Bucket löschen. Fehler nur an Sentry: Die
// Berechnung ist schon richtig gespeichert, eine verwaiste Datei stört nicht.
async function removeStoredSketch(pathOrUrl) {
  const path = pathOrUrl?.startsWith('http') ? sketchPathFromUrl(pathOrUrl) : pathOrUrl;
  if (!path) return;
  try {
    const { error } = await supabase.storage.from('sketches').remove([path]);
    if (error) reportError(error, { where: 'removeStoredSketch' });
  } catch (err) {
    reportError(err, { where: 'removeStoredSketch' });
  }
}

export const useProjectStore = create((set, get) => ({
  projects: [],
  currentProject: null,
  calculations: [],
  currentCalculation: null,
  loading: false,
  saving: false,
  // Message of the last failed save (manual or auto), null after a
  // successful one. The header chip shows "Nicht gespeichert" from it.
  saveError: null,
  uploadingSketch: false,

  // ─── Projects ──────────────────────────────────────────────────────

  fetchProjects: async () => {
    const headers = await authedHeaders();
    if (!headers) return;

    set({ loading: true });
    try {
      const res = await authFetch('/api/projects', { headers });
      const data = await readJsonOrThrow(res, 'Projekte laden');
      set({ projects: data });
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      set({ loading: false });
    }
  },

  createProject: async (projectData) => {
    const headers = await authedHeaders();
    if (!headers) throw new Error('Nicht angemeldet - bitte zuerst einloggen.');

    set({ saving: true });
    try {
      const res = await fetchWithTimeout('/api/projects', {
        method: 'POST',
        headers,
        body: JSON.stringify(projectData)
      });
      const data = await readJsonOrThrow(res, 'Projekt anlegen');
      set(state => ({ projects: [data, ...state.projects] }));
      return data;
    } catch (err) {
      console.error('Error creating project:', err);
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  selectProject: async (project) => {
    set({ currentProject: project, calculations: [], currentCalculation: null, saveError: null });
    endPreviewWithoutCalc();

    // Also clear the toleranceStore's TR-XXX badge — if the user had
    // a calc from a different project loaded, the header would keep
    // showing that stale "TR-003" even though no calculation is
    // selected in this project yet. Loading a calc from the new
    // project will set them again.
    useToleranceStore.setState({
      currentCalculationId: null,
      calculationIndex: null
    });

    if (project) {
      await get().fetchCalculations(project.id);
    }
  },

  /**
   * Clear the project/calc selection without picking a new project.
   * Used when the user creates a fresh chain from the header "Neu"
   * button — the next save will start a new project decision.
   */
  clearSelection: () => {
    set({ currentProject: null, currentCalculation: null, calculations: [], saveError: null });
    endPreviewWithoutCalc();
  },

  /**
   * Drop only the `currentCalculation` pointer, keep the project
   * selection. Used when loading a template / .tcp / starting a new
   * chain inside an open project — the chain is unrelated to whatever
   * calc was loaded before, so the next save must create a new row
   * (POST), not PATCH the stale id.
   *
   * Without this, the old PATCH path could 404 (id belongs to a
   * different project after a project switch) and crash the client
   * with "Unexpected end of JSON input".
   */
  detachCurrentCalculation: () => {
    set({ currentCalculation: null, saveError: null });
    // Header strip shows calculationIndex from toleranceStore — keep
    // it in sync so TR-003 doesn't keep glowing after detach.
    useToleranceStore.setState({
      currentCalculationId: null,
      calculationIndex: null
    });
  },

  /**
   * Updates a project's metadata (name, company, logo_url). All
   * three fields are optional — omitted fields stay as-is server-side.
   * Returns the updated project row.
   */
  updateProject: async (id, updates) => {
    const headers = await authedHeaders();
    if (!headers) return;

    set({ saving: true });
    try {
      const res = await authFetch(`/api/projects/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates)
      });
      const updated = await readJsonOrThrow(res, 'Projekt aktualisieren');
      set(state => ({
        projects: state.projects.map(p => p.id === id ? { ...p, ...updated } : p),
        currentProject: state.currentProject?.id === id
          ? { ...state.currentProject, ...updated }
          : state.currentProject
      }));
      return updated;
    } finally {
      set({ saving: false });
    }
  },

  /**
   * Rename a calculation without touching its `data` payload. Used by
   * the inline-rename UI in the calculation list view. Keeps the
   * current `data` intact — passing a fresh name straight to the
   * PATCH endpoint.
   */
  renameCalculation: async (calcId, name) => {
    const headers = await authedHeaders();
    const currentProject = get().currentProject;
    if (!headers || !currentProject) return;

    const res = await authFetch(
      `/api/projects/${currentProject.id}/calculations/${calcId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ name })
      }
    );
    const updated = await readJsonOrThrow(res, 'Berechnung umbenennen');
    set(state => ({
      calculations: state.calculations.map(c =>
        c.id === calcId ? { ...c, name: updated.name } : c
      ),
      currentCalculation: state.currentCalculation?.id === calcId
        ? { ...state.currentCalculation, name: updated.name }
        : state.currentCalculation
    }));
    // If the renamed calc is currently open in the editor, sync the
    // toleranceStore so the ProjectBar's name input reflects it too.
    if (useToleranceStore.getState().currentCalculationId === calcId) {
      useToleranceStore.setState({ calculationName: updated.name });
    }
    return updated;
  },

  deleteProject: async (id) => {
    const headers = await authedHeaders();
    if (!headers) return;

    try {
      const res = await authFetch(`/api/projects/${id}`, {
        method: 'DELETE',
        headers
      });
      // DELETE returns { success: true } — read and discard so any
      // server-side error message bubbles up.
      await readJsonOrThrow(res, 'Projekt löschen');
      set(state => ({
        projects: state.projects.filter(p => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
        currentCalculation: state.currentProject?.id === id ? null : state.currentCalculation,
        calculations: state.currentProject?.id === id ? [] : state.calculations
      }));
    } catch (err) {
      console.error('Error deleting project:', err);
      throw err;
    }
  },

  // ─── Calculations ──────────────────────────────────────────────────

  fetchCalculations: async (projectId) => {
    const headers = await authedHeaders();
    if (!headers) return;

    try {
      const res = await authFetch(`/api/projects/${projectId}/calculations`, { headers });
      const data = await readJsonOrThrow(res, 'Berechnungen laden');
      set({ calculations: data });
      return data;
    } catch (err) {
      console.error('Error fetching calculations:', err);
    }
  },

  /**
   * Save the current tolerance chain. If a `currentCalculation` is
   * already attached (loaded earlier), updates it via PATCH. Otherwise
   * creates a new calculation in the currently-selected project via
   * POST. The server-side trigger assigns the TR-XXX index on create.
   *
   * Marks the tolerance store clean after a successful save.
   * No-ops when there's no current project to save under.
   */
  saveCurrentCalculation: async () => {
    // Versions-Vorschau: ein alter Stand darf nie den aktuellen überschreiben.
    if (useToleranceStore.getState().preview) {
      throw new Error('In der Vorschau wird nicht gespeichert.');
    }
    // One save at a time. Manual save and auto-save can overlap (the
    // token lookup before the request may take a moment); two parallel
    // PATCHes could land in the wrong order on the server. A second
    // caller waits and then saves whatever is current by then.
    while (inFlightSave) {
      try { await inFlightSave; } catch { /* the first caller handles it */ }
    }
    inFlightSave = saveOnce(set, get);
    try {
      return await inFlightSave;
    } finally {
      inFlightSave = null;
    }
  },

  /**
   * Create a new, empty calculation in the current project and load
   * it into the tolerance store. Used by the "+ Neue Berechnung"
   * button in the ProjectBar / CalculationListView. Pro-only flow at
   * the server level.
   */
  createCalculation: async (overrides = {}) => {
    const headers = await authedHeaders();
    const currentProject = get().currentProject;
    if (!headers || !currentProject) return;

    set({ saving: true });
    try {
      // Empty data envelope — toleranceStore.loadProject will fall
      // back to defaults for every missing field.
      const body = {
        name: overrides.name || 'Neue Berechnung',
        data: overrides.data || {}
      };
      const res = await authFetch(`/api/projects/${currentProject.id}/calculations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      const newCalc = await readJsonOrThrow(res, 'Neue Berechnung anlegen');

      set(state => ({
        calculations: [...state.calculations, newCalc],
        currentCalculation: newCalc,
        saveError: null
      }));

      // Load the empty calculation into the tolerance store. Pro-mode
      // so structure stays editable.
      useToleranceStore.getState().loadProject(
        newCalc.data || {},
        newCalc.name,
        newCalc.id,
        newCalc.calculation_index,
        { proMode: true }
      );

      return newCalc;
    } catch (err) {
      console.error('Error creating calculation:', err);
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  /**
   * Dupliziert eine Berechnung im aktuellen Projekt (H20). Der Server
   * kopiert den gespeicherten Stand samt eigener Skizzen-Datei, ohne
   * Versionen. Ist genau diese Berechnung im Editor offen und noch nicht
   * gespeichert, wird vorher gespeichert, damit die Kopie aktuell ist.
   * Der Editor bleibt, wo er ist; die Kopie kommt nur in die Liste.
   * Returns { calculation, sketchCopied }; wirft bei Fehlern.
   */
  duplicateCalculation: async (calcId) => {
    const { currentCalculation, currentProject } = get();
    const tol = useToleranceStore.getState();
    if (currentCalculation?.id === calcId && tol.isDirty && !tol.preview) {
      await get().saveCurrentCalculation();
    }
    const headers = await authedHeaders();
    if (!headers || !currentProject) throw new Error('Nicht angemeldet');

    const res = await authFetch(
      `/api/projects/${currentProject.id}/calculations/${calcId}/duplicate`,
      { method: 'POST', headers }
    );
    const out = await readJsonOrThrow(res, 'Berechnung duplizieren');
    set(state => ({ calculations: [...state.calculations, out.calculation] }));
    return out;
  },

  deleteCalculation: async (calcId) => {
    const headers = await authedHeaders();
    const currentProject = get().currentProject;
    if (!headers || !currentProject) return;

    try {
      const res = await authFetch(
        `/api/projects/${currentProject.id}/calculations/${calcId}`,
        { method: 'DELETE', headers }
      );
      await readJsonOrThrow(res, 'Berechnung löschen');
      set(state => ({
        calculations: state.calculations.filter(c => c.id !== calcId),
        currentCalculation: state.currentCalculation?.id === calcId
          ? null : state.currentCalculation
      }));

      // If the user was viewing the deleted calc, clear the toolbox.
      const tol = useToleranceStore.getState();
      if (tol.currentCalculationId === calcId) {
        tol.newProject();
      }
    } catch (err) {
      console.error('Error deleting calculation:', err);
      throw err;
    }
  },

  /**
   * Loads a calculation into the tolerance store. Bumps the server-
   * side `last_opened_at` so the project view sorts correctly.
   */
  loadCalculation: async (calc) => {
    set({ currentCalculation: calc, saveError: null });

    const isPro = ['pro', 'team'].includes(
      useAuthStore.getState().profile?.plan
    );
    useToleranceStore.getState().loadProject(
      calc.data,
      calc.name,
      calc.id,
      calc.calculation_index,
      { proMode: isPro }
    );

    // Fire-and-forget — failure is non-fatal, sort order just won't
    // update. Don't block the UI on it.
    get().touchCalculation(calc.id).catch(() => {});
  },

  /**
   * PATCH the calculation's last_opened_at to NOW(). The backend
   * route accepts a sentinel `{ touch: true }` body so we don't have
   * to round-trip a timestamp from the client.
   */
  touchCalculation: async (calcId) => {
    const headers = await authedHeaders();
    const currentProject = get().currentProject;
    if (!headers || !currentProject) return;

    const res = await authFetch(
      `/api/projects/${currentProject.id}/calculations/${calcId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ touch: true })
      }
    );
    if (!res.ok) return; // silent — non-critical
    let updated;
    try { updated = await readJsonOrThrow(res, 'Last-opened'); }
    catch { return; }
    set(state => ({
      calculations: state.calculations.map(c =>
        c.id === calcId ? { ...c, last_opened_at: updated.last_opened_at } : c
      )
    }));
  },

  // ─── Sketch upload (Pro-only feature) ──────────────────────────────

  /**
   * Upload a sketch image for a calculation. Stored in the `sketches`
   * Supabase Storage bucket, then the public URL is PATCHed onto the
   * calculation row.
   *
   * Returns the new sketch URL on success; throws otherwise.
   */
  uploadSketch: async (calcId, file) => {
    const problem = checkSketchFile(file);
    if (problem) throw new Error(problem);

    const headers = await authedHeaders();
    const currentProject = get().currentProject;
    const user = useAuthStore.getState().user;
    if (!headers || !currentProject || !user) return;

    const oldUrl = sketchUrlOf(get(), calcId);
    set({ uploadingSketch: true });
    try {
      // Eigener Ordner <user_id>/ ist Pflicht laut Storage-Regel (017).
      const path = `${user.id}/${calcId}-${Date.now()}.${sketchExt(file.type)}`;
      const { error: uploadErr } = await supabase.storage
        .from('sketches')
        .upload(path, file, { upsert: false, contentType: file.type });
      if (uploadErr) {
        reportError(uploadErr, { where: 'uploadSketch' });
        throw new Error(SKETCH_UPLOAD_FAILED);
      }

      const { data: { publicUrl } } = supabase.storage.from('sketches').getPublicUrl(path);

      let updated;
      try {
        const res = await authFetch(
          `/api/projects/${currentProject.id}/calculations/${calcId}`,
          { method: 'PATCH', headers, body: JSON.stringify({ sketch_url: publicUrl }) }
        );
        updated = await readJsonOrThrow(res, 'Skizze speichern');
      } catch (err) {
        // Neue Datei wäre sonst verwaist; die alte Skizze bleibt gültig.
        await removeStoredSketch(path);
        throw err;
      }

      set(state => ({
        calculations: state.calculations.map(c =>
          c.id === calcId ? { ...c, sketch_url: updated.sketch_url } : c
        ),
        currentCalculation: state.currentCalculation?.id === calcId
          ? { ...state.currentCalculation, sketch_url: updated.sketch_url }
          : state.currentCalculation
      }));

      if (oldUrl && oldUrl !== updated.sketch_url) await removeStoredSketch(oldUrl);
      return updated.sketch_url;
    } finally {
      set({ uploadingSketch: false });
    }
  },

  removeSketch: async (calcId) => {
    const headers = await authedHeaders();
    const currentProject = get().currentProject;
    if (!headers || !currentProject) return;
    const oldUrl = sketchUrlOf(get(), calcId);

    const res = await authFetch(
      `/api/projects/${currentProject.id}/calculations/${calcId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ sketch_url: null })
      }
    );
    if (!res.ok) {
      await readJsonOrThrow(res, 'Skizze entfernen');
    }
    set(state => ({
      calculations: state.calculations.map(c =>
        c.id === calcId ? { ...c, sketch_url: null } : c
      ),
      currentCalculation: state.currentCalculation?.id === calcId
        ? { ...state.currentCalculation, sketch_url: null }
        : state.currentCalculation
    }));
    await removeStoredSketch(oldUrl);
  }
}));
